import React, { useEffect, useMemo, useState } from "react";
import {
    ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    LineChart, Line,
} from "recharts";
import { http, errMsg } from "@/lib/api";
import { SACard, formatBytes } from "@/components/superadmin/SAPrimitives";

const PALETTE = [
    "#00C2FF", "#7dd3fc", "#34d399", "#fbbf24", "#f472b6",
    "#a78bfa", "#fb923c", "#60a5fa", "#f87171", "#2dd4bf",
    "#94a3b8", // reserved for "others"
];

const DAY_OPTIONS = [7, 14, 30, 60, 90];

export default function SuperAdminAnalytics() {
    const [days, setDays] = useState(30);
    const [top, setTop] = useState(6);
    const [perTenant, setPerTenant] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        (async () => {
            try {
                const [pt, js] = await Promise.all([
                    http.get(`/superadmin/analytics/per-tenant-timeseries?days=${days}&top=${top}`),
                    http.get(`/superadmin/analytics/jobs-timeseries?days=${days}`),
                ]);
                if (!alive) return;
                setPerTenant(pt.data);
                setJobs(js.data.series || []);
                setErr("");
            } catch (e) {
                if (alive) setErr(errMsg(e));
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [days, top]);

    // recharts wants data keyed by series id — we already return that shape.
    const seriesKeys = perTenant?.series_keys || [];
    const keyToName = useMemo(() => {
        const m = {};
        (perTenant?.top_tenants || []).forEach((t) => { m[t.id] = t.name; });
        m["__others__"] = "Others";
        return m;
    }, [perTenant]);

    return (
        <div className="space-y-5" data-testid="sa-analytics-page">
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="text-[24px] font-semibold tracking-tight">Platform Analytics</h1>
                    <div className="mt-1 text-[12px] text-white/55">
                        Multi-tenant stacked timeseries · top {top} tenants by volume
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        data-testid="sa-analytics-days"
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value))}
                        className="h-8 rounded-md border border-white/5 bg-white/[0.03] px-2 text-[12px] text-white focus:outline-none"
                    >
                        {DAY_OPTIONS.map((d) => (
                            <option key={d} value={d}>Last {d} days</option>
                        ))}
                    </select>
                    <select
                        data-testid="sa-analytics-top"
                        value={top}
                        onChange={(e) => setTop(Number(e.target.value))}
                        className="h-8 rounded-md border border-white/5 bg-white/[0.03] px-2 text-[12px] text-white focus:outline-none"
                    >
                        {[3, 6, 10, 15].map((n) => <option key={n} value={n}>Top {n}</option>)}
                    </select>
                </div>
            </div>

            {err && <SACard className="text-red-300">{err}</SACard>}

            <SACard data-testid="sa-analytics-per-tenant">
                <div className="mb-3 flex items-center justify-between">
                    <div>
                        <div className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-white/50">
                            Data Transfer · per tenant
                        </div>
                        <div className="mt-1 text-[13px] text-white/65">
                            Stacked by tenant · bytes completed per day
                        </div>
                    </div>
                </div>
                <div className="h-[360px]" data-testid="sa-analytics-stacked-chart">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={perTenant?.rows || []} margin={{ top: 4, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                            <XAxis
                                dataKey="day"
                                stroke="rgba(255,255,255,0.35)"
                                tick={{ fontSize: 10 }}
                                tickFormatter={(d) => d?.slice(5) || ""}
                            />
                            <YAxis
                                stroke="rgba(255,255,255,0.35)"
                                tick={{ fontSize: 10 }}
                                tickFormatter={(v) => formatBytes(v)}
                                width={70}
                            />
                            <Tooltip
                                contentStyle={{
                                    background: "#0b1226",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                    borderRadius: 6,
                                    fontSize: 11,
                                }}
                                labelStyle={{ color: "#fff" }}
                                formatter={(val, key) => [formatBytes(val), keyToName[key] || key]}
                            />
                            <Legend
                                wrapperStyle={{ fontSize: 10, color: "rgba(255,255,255,0.7)" }}
                                formatter={(key) => keyToName[key] || key}
                            />
                            {seriesKeys.map((key, i) => (
                                <Area
                                    key={key}
                                    type="monotone"
                                    dataKey={key}
                                    name={key}
                                    stackId="1"
                                    stroke={key === "__others__" ? "#94a3b8" : PALETTE[i % (PALETTE.length - 1)]}
                                    fill={key === "__others__" ? "#94a3b8" : PALETTE[i % (PALETTE.length - 1)]}
                                    fillOpacity={0.45}
                                    strokeWidth={1.2}
                                />
                            ))}
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
                {!loading && (perTenant?.rows?.length || 0) === 0 && (
                    <div className="py-10 text-center text-[12px] text-white/40">
                        No completed-job activity in this window.
                    </div>
                )}
            </SACard>

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                <SACard data-testid="sa-analytics-jobs-lines">
                    <div className="mb-3 text-[10.5px] font-medium uppercase tracking-[0.14em] text-white/50">
                        Jobs · completed vs failed · per day
                    </div>
                    <div className="h-[240px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={jobs} margin={{ top: 4, right: 10, left: -10, bottom: 0 }}>
                                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                                <XAxis dataKey="day" stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 10 }} tickFormatter={(d) => d?.slice(5) || ""} />
                                <YAxis stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 10 }} />
                                <Tooltip contentStyle={{ background: "#0b1226", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, fontSize: 11 }} />
                                <Legend wrapperStyle={{ fontSize: 10, color: "rgba(255,255,255,0.7)" }} />
                                <Line type="monotone" dataKey="completed" stroke="#34d399" strokeWidth={1.6} dot={false} />
                                <Line type="monotone" dataKey="failed" stroke="#f87171" strokeWidth={1.6} dot={false} />
                                <Line type="monotone" dataKey="running" stroke="#00C2FF" strokeWidth={1.6} dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </SACard>

                <SACard data-testid="sa-analytics-top-legend">
                    <div className="mb-3 text-[10.5px] font-medium uppercase tracking-[0.14em] text-white/50">
                        Top tenants · this window
                    </div>
                    <ul className="space-y-2">
                        {(perTenant?.top_tenants || []).map((t, i) => (
                            <li key={t.id} className="flex items-center gap-2 text-[12.5px]">
                                <span
                                    className="h-3 w-3 rounded-sm"
                                    style={{ background: PALETTE[i % (PALETTE.length - 1)] }}
                                />
                                <span className="truncate text-white/85">{t.name}</span>
                            </li>
                        ))}
                        {(perTenant?.top_tenants?.length || 0) === 0 && (
                            <li className="text-[12px] text-white/40">No data yet.</li>
                        )}
                    </ul>
                </SACard>
            </div>
        </div>
    );
}
